import React, { useEffect, useMemo, useState } from 'react'
import { Button, Table, Row, Col, Empty, Tooltip } from 'antd'
import {
  DollarOutlined, ExportOutlined, ThunderboltOutlined,
  RiseOutlined, PercentageOutlined, FundOutlined,
} from '@ant-design/icons'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts'
import dayjs, { Dayjs } from 'dayjs'
import { mappingStore, type GeneratorMapping } from '@/pages/Mapping/mappingStore'
import { useMasterData } from '@/context/MasterDataContext'

/* ── Revenue model constants ─────────────────────────────────────────────
   Units are derived from each plant's Proposed Annual Generation (kWh/year)
   spread evenly across 12 months, and how much of that is allocated to each
   consumer (shareHolding). Revenue = units × tariff; trade margin = units ×
   tradeMargin. A 12-month seasonality curve makes the date filter produce
   realistic, period-specific figures.                                        */
const SEASON = [0.96, 0.90, 0.98, 1.08, 1.16, 1.18, 1.10, 1.02, 0.93, 0.97, 1.05, 1.12] // Jan→Dec
const PALETTE = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899', '#84cc16', '#f97316']

type Dim = 'plant' | 'generator' | 'consumer'

interface BaseRow {
  generatorId: string
  generatorName: string
  plantType: string
  consumerId: string
  consumerName: string
  unitsBase: number   // base monthly units (before seasonality)
  tariff: number
  margin: number
}

interface DimRow {
  key: string
  name: string
  units: number
  revenue: number
  marginAmt: number
  share: number       // % of total revenue
}

const parseAnnualGenerationKWh = (s: string): number => {
  const num = parseFloat(String(s).replace(/[^0-9.]/g, '')) || 0
  return /mwh/i.test(s) ? num * 1000 : num // anything not flagged MWh is treated as kWh
}

const fmtINR = (v: number): string =>
  v >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr`
  : v >= 1e5 ? `₹${(v / 1e5).toFixed(1)} L`
  : `₹${Math.round(v).toLocaleString('en-IN')}`

const fmtUnits = (kwh: number): string =>
  kwh >= 1e6 ? `${(kwh / 1e6).toFixed(2)} GWh`
  : kwh >= 1e3 ? `${(kwh / 1e3).toFixed(1)} MWh`
  : `${Math.round(kwh).toLocaleString('en-IN')} kWh`

// Seasonality contribution of a single calendar day (month curve ÷ days in month)
const dayFactor = (d: Dayjs): number => (SEASON[d.month()] ?? 1) / d.daysInMonth()

// Sum of day-factors across an inclusive [s, e] range — this IS the period scale
// (e.g. a full average month ≈ 1.0, one week ≈ 0.23, a single day ≈ 0.03).
const factorSum = (s: Dayjs, e: Dayjs): number => {
  let sum = 0
  let cur = s.startOf('day')
  const last = e.startOf('day')
  let guard = 0
  while (!cur.isAfter(last) && guard < 2000) {
    sum += dayFactor(cur)
    cur = cur.add(1, 'day')
    guard++
  }
  return sum
}

interface Props {
  /** Filter to a single generator/plant id (from the dashboard PLANT pills); 'all' = no filter */
  plantFilter?: string
  /** Date range from the dashboard's global PERIOD filter (single source of truth). */
  range?: [Dayjs, Dayjs]
  /** Human label for that period (e.g. "This Month", "24 May – 22 Jun '26"). */
  periodLabel?: string
}

const RevenueAnalytics: React.FC<Props> = ({ plantFilter = 'all', range, periodLabel = 'This Month' }) => {
  // Live mapping data (generator ↔ consumer allocations with tariffs/margins)
  const [mappings, setMappings] = useState<GeneratorMapping[]>(() => mappingStore.getAll())
  useEffect(() => {
    const refresh = () => setMappings(mappingStore.getAll())
    window.addEventListener(mappingStore.eventName, refresh)
    return () => window.removeEventListener(mappingStore.eventName, refresh)
  }, [])

  // "By Plant" groups against the Power Type master, not the mapping's raw
  // free-text plantType — that field is copied from whatever a generator/plant
  // was typed as at mapping time (see ConsumerAllocationEditor's plantType
  // build), which can be a stray value ("very power"), differently-cased
  // duplicate ("solar" vs "Solar Power"), or a joined hybrid ("Solar Power +
  // Wind Power"). Same fuzzy substring match Dashboard/index.tsx's Generation
  // Trend chart already uses against the master, so both charts classify the
  // same raw data the same way.
  const { powerTypes } = useMasterData()
  const activePowerTypes = useMemo(() => powerTypes.filter(pt => pt.status === 'active'), [powerTypes])
  const classifyPlantType = (raw: string): string[] => {
    const whole = raw.trim().toLowerCase()
    if (!whole) return ['Unspecified']
    const wholeMatch = activePowerTypes.find(pt => {
      const n = pt.name.trim().toLowerCase()
      return !!n && (whole.includes(n) || n.includes(whole))
    })
    if (wholeMatch) return [wholeMatch.name]
    // No single match — the string may join multiple types ("A + B"); try
    // classifying each token and keep only the ones that resolve.
    const tokens = raw.split('+').map(t => t.trim().toLowerCase()).filter(Boolean)
    const matched = new Set<string>()
    tokens.forEach(t => {
      const m = activePowerTypes.find(pt => {
        const n = pt.name.trim().toLowerCase()
        return !!n && (t.includes(n) || n.includes(t))
      })
      if (m) matched.add(m.name)
    })
    return matched.size ? [...matched] : ['Unspecified']
  }

  const [dim, setDim] = useState<Dim>('plant')

  // Normalised date range (defaults to the current month if none supplied)
  const normRange = useMemo<[Dayjs, Dayjs]>(() => {
    const s = range?.[0] ?? dayjs().startOf('month')
    const e = range?.[1] ?? dayjs().endOf('month')
    return e.isBefore(s) ? [e, s] : [s, e]
  }, [range])

  // ── Base allocation rows (one per generator→consumer link) ──────────────
  const baseRows = useMemo<BaseRow[]>(() => {
    const out: BaseRow[] = []
    mappings.filter(m => plantFilter === 'all' || m.generatorId === plantFilter).forEach(m => {
      const annualGenKWh = parseAnnualGenerationKWh(m.capacity)
      const monthlyBase = annualGenKWh / 12
      const plantTypes = classifyPlantType(m.plantType || '')
      m.consumers.filter(c => c.status === 'active').forEach(c => {
        const unitsBase = monthlyBase * (c.shareHolding / 100)
        // A hybrid plant (multiple resolved types) splits its units evenly
        // across each — mapping/generator records don't carry a per-type
        // capacity split, so an even split is the best available estimate.
        plantTypes.forEach(plantType => {
          out.push({
            generatorId: m.generatorId,
            generatorName: m.generatorName,
            plantType,
            consumerId: c.consumerId,
            consumerName: c.consumerName,
            unitsBase: unitsBase / plantTypes.length,
            tariff: c.tariff,
            margin: c.tradeMargin,
          })
        })
      })
    })
    return out
  }, [mappings, plantFilter, activePowerTypes])

  // Period scale derived purely from the dashboard date range
  const periodFactor = useMemo(() => factorSum(normRange[0], normRange[1]), [normRange])

  // Per-day base figures (for the trend chart) — constant across the period
  const sumUnitsTariff = useMemo(() => baseRows.reduce((s, r) => s + Number(r.unitsBase) * Number(r.tariff), 0), [baseRows])
  const sumUnitsMargin = useMemo(() => baseRows.reduce((s, r) => s + Number(r.unitsBase) * Number(r.margin), 0), [baseRows])

  // ── Totals for the selected period ──────────────────────────────────────
  const totals = useMemo(() => {
    let revenue = 0, units = 0, marginAmt = 0
    baseRows.forEach(r => {
      const u = r.unitsBase * periodFactor
      units += u
      revenue += u * r.tariff
      marginAmt += u * r.margin
    })
    return { revenue, units, marginAmt, avgRate: units ? revenue / units : 0 }
  }, [baseRows, periodFactor])

  // ── Breakdown by the selected dimension ─────────────────────────────────
  const dimRows = useMemo<DimRow[]>(() => {
    const map = new Map<string, DimRow>()
    baseRows.forEach(r => {
      const key = dim === 'plant' ? r.plantType : dim === 'generator' ? r.generatorId : r.consumerId
      const name = dim === 'plant' ? r.plantType : dim === 'generator' ? r.generatorName : r.consumerName
      const u = r.unitsBase * periodFactor
      const rev = u * r.tariff
      const mg = u * r.margin
      const cur = map.get(key) || { key, name, units: 0, revenue: 0, marginAmt: 0, share: 0 }
      cur.units += u; cur.revenue += rev; cur.marginAmt += mg
      map.set(key, cur)
    })
    const list = Array.from(map.values()).sort((a, b) => b.revenue - a.revenue)
    const tot = list.reduce((s, r) => s + Number(r.revenue), 0) || 1
    list.forEach(r => { r.share = (r.revenue / tot) * 100 })
    return list
  }, [baseRows, periodFactor, dim])

  // ── Trend series — daily for short ranges, weekly mid, monthly for long ──
  const trend = useMemo(() => {
    const [rs, re] = normRange
    const days = Math.max(1, re.startOf('day').diff(rs.startOf('day'), 'day') + 1)
    const mk = (label: string, f: number) => ({ label, revenue: Math.round(f * sumUnitsTariff), margin: Math.round(f * sumUnitsMargin) })
    const out: { label: string; revenue: number; margin: number }[] = []

    if (days <= 14) {
      let cur = rs.startOf('day')
      for (let i = 0; i < days; i++) {
        out.push(mk(cur.format('DD MMM'), dayFactor(cur)))
        cur = cur.add(1, 'day')
      }
    } else if (days <= 92) {
      let cur = rs.startOf('day')
      const end = re.startOf('day')
      let guard = 0
      while (!cur.isAfter(end) && guard < 60) {
        const wEnd = cur.add(6, 'day')
        const bEnd = wEnd.isAfter(end) ? end : wEnd
        out.push(mk(cur.format('DD MMM'), factorSum(cur, bEnd)))
        cur = bEnd.add(1, 'day')
        guard++
      }
    } else {
      let cur = rs.startOf('month')
      const end = re.startOf('month')
      let guard = 0
      while (!cur.isAfter(end) && guard < 60) {
        const ms = cur.startOf('month').isBefore(rs) ? rs : cur.startOf('month')
        const me = cur.endOf('month').isAfter(re) ? re : cur.endOf('month')
        out.push(mk(cur.format("MMM 'YY"), factorSum(ms, me)))
        cur = cur.add(1, 'month')
        guard++
      }
    }
    return out
  }, [normRange, sumUnitsTariff, sumUnitsMargin])

  // Donut: top 6 + "Others"
  const pieData = useMemo(() => {
    if (dimRows.length <= 7) return dimRows.map(r => ({ name: r.name, value: Math.round(r.revenue) }))
    const top = dimRows.slice(0, 6).map(r => ({ name: r.name, value: Math.round(r.revenue) }))
    const others = dimRows.slice(6).reduce((s, r) => s + Number(r.revenue), 0)
    return [...top, { name: 'Others', value: Math.round(others) }]
  }, [dimRows])

  const dimNoun = dim === 'plant' ? 'Plant Type' : dim === 'generator' ? 'Generator' : 'Consumer'

  // ── CSV export of the current breakdown ─────────────────────────────────
  const exportCsv = () => {
    const header = [dimNoun, 'Units (kWh)', 'Revenue (INR)', 'Trade Margin (INR)', 'Share (%)']
    const lines = dimRows.map(r => [
      `"${r.name}"`, Math.round(r.units), Math.round(r.revenue), Math.round(r.marginAmt), r.share.toFixed(1),
    ].join(','))
    const csv = [`Revenue by ${dimNoun} — ${periodLabel}`, header.join(','), ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `revenue-by-${dim}-${periodLabel.replace(/\s+/g, '-')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const summary = [
    { label: 'Total Revenue', value: fmtINR(totals.revenue), color: '#6366f1', icon: <DollarOutlined />, sub: 'Billed to consumers' },
    { label: 'Units Delivered', value: fmtUnits(totals.units), color: '#0ea5e9', icon: <ThunderboltOutlined />, sub: 'Energy supplied' },
    { label: 'Trade Margin', value: fmtINR(totals.marginAmt), color: '#10b981', icon: <RiseOutlined />, sub: 'Shakthi Power earning' },
    { label: 'Avg Rate', value: `₹${totals.avgRate.toFixed(2)}`, color: '#f59e0b', icon: <PercentageOutlined />, sub: 'per kWh' },
  ]

  const maxRev = dimRows.length ? dimRows[0].revenue : 1

  const columns = [
    {
      title: dimNoun,
      dataIndex: 'name',
      render: (v: string, _r: DimRow, i: number) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#0b1426', fontSize: 13 }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
          {v}
        </span>
      ),
    },
    { title: 'Units', dataIndex: 'units', align: 'right' as const, render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#4a5568' }}>{fmtUnits(v)}</span> },
    { title: 'Revenue', dataIndex: 'revenue', align: 'right' as const, render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0b1426' }}>{fmtINR(v)}</span> },
    { title: 'Margin', dataIndex: 'marginAmt', align: 'right' as const, render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#10b981' }}>{fmtINR(v)}</span> },
    { title: 'Share', dataIndex: 'share', align: 'right' as const, render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#8a93a6' }}>{v.toFixed(1)}%</span> },
  ]

  const hasData = baseRows.length > 0

  return (
    <div className="dash-analytics-card" style={{ marginBottom: 16 }}>
      {/* ── Header ── */}
      <div className="dash-analytics-header" style={{ flexWrap: 'wrap', gap: 12, paddingBottom: 16 }}>
        <div className="dash-analytics-title-row">
          <FundOutlined className="dash-analytics-icon" style={{ color: '#10b981' }} />
          <span className="dash-analytics-title">Revenue Analytics</span>
          <span style={{ fontSize: 12, color: '#8a93a6', fontWeight: 600, marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#9aa3af', fontWeight: 500 }}>Uses the dashboard period above</span>
          <Tooltip title="Export current breakdown to CSV">
            <Button size="small" icon={<ExportOutlined />} className="dash-icon-btn" onClick={exportCsv}>Export</Button>
          </Tooltip>
        </div>
      </div>

      {!hasData ? (
        <div style={{ padding: 40 }}>
          <Empty description="No mapping/allocation data to compute revenue. Add a generator↔consumer mapping first." />
        </div>
      ) : (
        <div style={{ padding: '0 20px 20px' }}>
          {/* ── Summary KPIs ── */}
          <div className="dash-mini-stats" style={{ marginBottom: 18 }}>
            {summary.map(s => (
              <div key={s.label} className="dash-mini-stat" style={{ borderTopColor: s.color, textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: s.color, fontSize: 13 }}>
                  {s.icon}
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#8a93a6', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</span>
                </div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 22, fontWeight: 800, color: s.color, marginTop: 6 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#8a93a6', marginTop: 2 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Revenue trend ── */}
          <div className="dash-chart-section">
            <div className="dash-chart-header">
              <div>
                <h3 className="dash-chart-title">Revenue Trend</h3>
                <p className="dash-chart-sub">Revenue vs trade margin — {periodLabel}</p>
              </div>
              <div className="dash-legend">
                <span className="dash-legend-item"><span className="dash-legend-dot" style={{ background: '#6366f1' }} />Revenue</span>
                <span className="dash-legend-item"><span className="dash-legend-dot" style={{ background: '#10b981' }} />Margin</span>
              </div>
            </div>
            <div style={{ height: 260, width: '100%', marginTop: 18 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e8f0" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#8a93a6' }} dy={8} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#8a93a6' }} tickFormatter={(v: number) => v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : `${v}`} />
                  <RTooltip
                    cursor={{ fill: '#f1f5f9' }}
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    formatter={(v: any, n: any) => [fmtINR(Number(v)), n === 'revenue' ? 'Revenue' : 'Margin']}
                  />
                  <Bar dataKey="revenue" name="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={trend.length > 8 ? 12 : 22} />
                  <Bar dataKey="margin" name="margin" fill="#10b981" radius={[4, 4, 0, 0]} barSize={trend.length > 8 ? 12 : 22} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Dimension breakdown ── */}
          <div style={{ marginTop: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
              <h3 className="dash-section-title" style={{ margin: 0 }}>Revenue by {dimNoun}</h3>
              <span className="dash-pill-group">
                {[
                  { label: 'By Plant', value: 'plant' },
                  { label: 'By Generator', value: 'generator' },
                  { label: 'By Consumer', value: 'consumer' },
                ].map((o) => (
                  <button
                    key={o.value}
                    className={`dash-pill dash-pill-sm${dim === o.value ? ' active' : ''}`}
                    onClick={() => setDim(o.value as Dim)}
                  >
                    {o.label}
                  </button>
                ))}
              </span>
            </div>

            <Row gutter={[20, 16]}>
              {/* Ranked bars */}
              <Col xs={24} lg={14}>
                {dimRows.map((r, i) => (
                  <div key={r.key} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#0b1426', display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: PALETTE[i % PALETTE.length] }} />
                        {r.name}
                      </span>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: '#0b1426' }}>
                        {fmtINR(r.revenue)} <span style={{ color: '#8a93a6', fontWeight: 500 }}>· {r.share.toFixed(1)}%</span>
                      </span>
                    </div>
                    <div style={{ height: 10, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ width: `${maxRev ? (r.revenue / maxRev) * 100 : 0}%`, height: '100%', background: PALETTE[i % PALETTE.length], borderRadius: 6, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                ))}
              </Col>

              {/* Donut share */}
              <Col xs={24} lg={10}>
                <div style={{ height: 240, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={88} paddingAngle={2}>
                        {pieData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v: any, n: any) => [fmtINR(Number(v)), n]} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>

            {/* Detail table */}
            <Table
              className="dash-tab-content"
              style={{ marginTop: 12 }}
              dataSource={dimRows}
              columns={columns}
              rowKey="key"
              size="small"
              pagination={false}
              summary={() => (
                <Table.Summary.Row style={{ background: '#fafbfd' }}>
                  <Table.Summary.Cell index={0}><span style={{ fontWeight: 800, color: '#0b1426' }}>Total</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right"><span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{fmtUnits(totals.units)}</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right"><span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, color: '#6366f1' }}>{fmtINR(totals.revenue)}</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right"><span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, color: '#10b981' }}>{fmtINR(totals.marginAmt)}</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right"><span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#8a93a6' }}>100%</span></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default RevenueAnalytics
